// kategorije koje korisnik bira na sajtu (iste za sve, ulogovane i goste);
// cene su privremeno skrivene (jos nema slanja za cene) - vratiti: { value: 'market_prices', label: 'Cene na pijacama' }
export const PUSH_CATEGORY_OPTIONS = [
  { value: 'news', label: 'Vesti' },
  { value: 'events', label: 'Događaji' },
  { value: 'recipes', label: 'Recepti' },
  { value: 'advertisements', label: 'Oglasi' },
]

export const PUSH_ALL_CATEGORIES = PUSH_CATEGORY_OPTIONS.map((item) => item.value)

export const usePushNotifications = () => {
  const config = useRuntimeConfig()
  const { $api, $apiAuth } = useNuxtApp() as any

  const permission = ref<NotificationPermission | 'unsupported'>('default')
  const state = ref<{ is_active: boolean, linked: boolean, categories: string[] } | null>(null)

  const isSupported = () =>
    import.meta.client && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

  const urlBase64ToUint8Array = (base64: string) => {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4)
    const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'))
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
  }

  // 'default' = korisnik jos nije odgovorio browseru, 'granted', 'denied' ili 'unsupported'
  const currentPermission = () => (isSupported() ? Notification.permission : 'unsupported')

  const getRegistration = async () => {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    return navigator.serviceWorker.ready
  }

  const appServerKey = () => urlBase64ToUint8Array(config.public.VAPID_PUBLIC_KEY as string)

  // Pretplata napravljena drugim VAPID ključem ne može da primi poruke (push servis vraća 403).
  // Ako browser ne otkriva ključ (null), ne diramo pretplatu.
  const sameKey = (current: ArrayBuffer | null | undefined, expected: Uint8Array) => {
    if (!current) return true
    const bytes = new Uint8Array(current)
    return bytes.length === expected.length && bytes.every((b, i) => b === expected[i])
  }

  // Dozvola je već data (granted), ali uređaj nema pretplatu ili je ona pod pogrešnim ključem.
  // Bez ovoga takav uređaj nikad ne bi dobio ponudu da se prijavi (kartica traži permission 'default').
  async function needsSubscription() {
    if (!isSupported() || Notification.permission !== 'granted') return false

    try {
      const info = await inspect()
      return !info.hasSubscription || !info.keyMatches
    } catch (e) {
      return false
    }
  }

  // stanje pretplate ovog uredjaja (bez menjanja ičega) - koristi se i na stranici /push-test
  async function inspect() {
    const registration = isSupported() ? await navigator.serviceWorker.getRegistration('/') : undefined
    const subscription = await registration?.pushManager.getSubscription()
    let endpointHost: string | null = null

    try {
      endpointHost = subscription ? new URL(subscription.endpoint).host : null
    } catch (e) { /* neispravan endpoint */ }

    return {
      supported: isSupported(),
      permission: currentPermission(),
      hasWorker: !!registration,
      hasSubscription: !!subscription,
      keyMatches: subscription ? sameKey(subscription.options?.applicationServerKey, appServerKey()) : false,
      endpointHost
    }
  }

  const getSubscription = async () => (await getRegistration()).pushManager.getSubscription()

  const contentEncoding = () =>
    ((PushManager as any).supportedContentEncodings || []).includes('aes128gcm') ? 'aes128gcm' : 'aesgcm'

  // mora se pozvati direktno iz klika (zahtev za dozvolu)
  // bez categories: uređaj se samo prijavi i veže za nalog, postojeće kategorije ostaju
  async function subscribe(categories?: string[]) {
    if (!isSupported()) { permission.value = 'unsupported'; return null }

    permission.value = await Notification.requestPermission()
    if (permission.value !== 'granted') return null

    const registration = await getRegistration()
    const expectedKey = appServerKey()
    let subscription = await registration.pushManager.getSubscription()

    // pretplata pod drugim ključem se odjavljuje i pravi se nova sa trenutnim
    if (subscription && !sameKey(subscription.options?.applicationServerKey, expectedKey)) {
      await subscription.unsubscribe()
      subscription = null
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: expectedKey
      })
    }

    const json = subscription.toJSON()
    // use: 'customer' -> salje token kupca ako je ulogovan, inace ide anonimno
    const res = await $apiAuth('/api/push/device', {
      method: 'POST',
      use: 'customer',
      body: {
        endpoint: json.endpoint,
        keys: json.keys,
        content_encoding: contentEncoding(),
        ...(categories ? { categories } : {})
      }
    })
    state.value = res.data
    return res.data
  }

  async function callWithSubscription(path: string) {
    if (!isSupported()) return null
    const subscription = await getSubscription()
    if (!subscription) { state.value = null; return null }
    const json = subscription.toJSON()
    const res = await $api(path, { method: 'POST', body: { endpoint: json.endpoint, auth: json.keys?.auth } })
    state.value = res.data
    return res.data
  }

  // obaveštenja samo za nalog (npr. kada admin odobri oglas) - bez menjanja kategorija
  const enableForAccount = () => subscribe()

  const status = () => callWithSubscription('/api/push/device/status')
  const unlink = () => callWithSubscription('/api/push/device/unlink')

  async function unsubscribe() {
    const data = await callWithSubscription('/api/push/device/unsubscribe')
    await (await getSubscription())?.unsubscribe()
    return data
  }

  return { permission, state, isSupported, currentPermission, needsSubscription, inspect, subscribe, enableForAccount, status, unlink, unsubscribe }
}
